import Cookies from 'js-cookie';
import { isEqual } from 'lodash';
import { useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';

import { useListenToApp } from '@hivebrite/app-web-bridge';
import { Box } from '@hivebrite/design-system/Box';
import { Button, ButtonClose } from '@hivebrite/design-system/Button';
import { DefaultLink } from '@hivebrite/design-system/Link';
import { primaryShadow } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useCookieList } from 'hb-react/shared/apps/CookieBanner/hooks/useCookieList';
import { useFocusTrap } from 'hb-react/shared/apps/CookieBanner/hooks/useFocusTrap';
import { checkCanConnectAmplitude } from 'hb-react/shared/apps/CookieBanner/utils/amplitude/cookieUtils';
import { removeNonRequiredCookies } from 'hb-react/shared/apps/CookieBanner/utils/manageRequiredCookies';
import useFrameTheme from 'hb-react/shared/hooks/theme/useFrameTheme';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import emitter from 'hb-react/shared/utils/emitter';
import ThemeProvider from 'hb-react/style/ThemeProvider';
import { initGoogle } from 'initializers/google';

import CustomiseSettingsModal from './CustomiseSettingsModal';

const COOKIES_EXPIRATION_DAYS = 120;

const CookieBanner = ({ env, connectAmplitude }) => {
  const [bannerElement, setBannerElement] = useState(null);
  const setBannerRef = useCallback((el) => {
    setBannerElement(el);
  }, []);
  const { currentNetwork } = useDefaultAppContexts();
  const frameTheme = useFrameTheme();
  const { flagsLoaded } = useFeatureFlag();
  const { cookieLists, cookieParams } = useCookieList(env);

  const [displayBanner, setDisplayBanner] = useState(false);
  const [displayModal, setDisplayModal] = useState(false);
  const cookieConfig = currentNetwork.features.cookies.config;
  const cookieName = `cookie_consent_list_${env === 'po' ? 'fo' : env}`;

  const canConnectAmplitude = checkCanConnectAmplitude({
    amplitudeIsEnabled: cookieParams.amplitudeIsEnabled,
    env,
  });

  useEffect(() => {
    const isCookie = Cookies.get(cookieName);

    if (isCookie !== undefined) {
      const userCookiesConsentList =
        env === 'bo'
          ? JSON.parse(Cookies.get('cookie_consent_list_bo'))
          : JSON.parse(Cookies.get('cookie_consent_list_fo'));

      Object.keys(userCookiesConsentList).forEach((key) => {
        userCookiesConsentList[key].forEach((item) => (item.accepted = true));
      });

      // This condition allow us to remove non required cookies from the user browser.
      // By keeping the required cookies in his browser the session stays open so he doesn't need to relog.
      // As setDisplayBanner is not called, it's value is true so even if the user still has a cookie named cookie_consent_list_env (fo / bo) the banner will pop,
      // so he can reselect his cookie preferences and so we can overwrite the existing cookie_consent_list_env.
      if (
        Object.keys(userCookiesConsentList).length ===
          Object.keys(cookieLists.cookieServiceNameList).length &&
        Object.keys(userCookiesConsentList).every((key) =>
          isEqual(
            userCookiesConsentList[key],
            cookieLists.cookieServiceNameList[key],
          ),
        )
      ) {
        setDisplayBanner(false);
      } else {
        setDisplayBanner(true);
        removeNonRequiredCookies(cookieLists);
      }
    } else {
      setDisplayBanner(true);
      removeNonRequiredCookies(cookieLists);
    }
  }, [cookieLists, cookieName, env]);

  const handleClick = useCallback(
    (type, customList) => {
      const newCookieServiceNameList = { ...cookieLists.cookieServiceNameList };
      const cookieOptions = {
        expires: COOKIES_EXPIRATION_DAYS,
      };

      switch (type) {
        case 'acceptAll':
          Cookies.set(
            cookieName,
            JSON.stringify(cookieLists.cookieServiceNameList),
            cookieOptions,
          );
          if (env !== 'bo') {
            initGoogle();
          }
          if (canConnectAmplitude) {
            connectAmplitude();
          }
          setDisplayBanner(false);
          break;
        case 'acceptRequired':
          newCookieServiceNameList.functional.forEach(
            (item) => (item.accepted = false),
          );
          newCookieServiceNameList.tracking.forEach(
            (item) => (item.accepted = false),
          );
          Cookies.set(
            cookieName,
            JSON.stringify(newCookieServiceNameList),
            cookieOptions,
          );
          setDisplayBanner(false);
          break;
        case 'customiseSettings':
          customList.forEach((item) => {
            if (item.type === 'functional') {
              newCookieServiceNameList.functional.forEach(
                (funcItem) => (funcItem.accepted = item.accepted),
              );
            }
            if (item.type === 'tracking') {
              newCookieServiceNameList.tracking.forEach(
                (trackItem) => (trackItem.accepted = item.accepted),
              );
            }
          });
          Cookies.set(
            cookieName,
            JSON.stringify(newCookieServiceNameList),
            cookieOptions,
          );
          setDisplayModal(false);
          if (newCookieServiceNameList.tracking.length > 0) {
            if (
              cookieParams.hasGoogleAnalyticsTracking &&
              newCookieServiceNameList.tracking.find(
                ({ name }) => name === 'Google analytics',
              )?.accepted === true &&
              env !== 'bo'
            ) {
              initGoogle();
            }
            if (
              canConnectAmplitude &&
              newCookieServiceNameList.tracking.find(
                ({ name }) => name === 'Amplitude (Trackers)',
              )?.accepted === true
            ) {
              connectAmplitude();
            }
          }
          setDisplayBanner(false);
          break;
        default:
          return null;
      }
    },
    [
      canConnectAmplitude,
      connectAmplitude,
      cookieLists.cookieServiceNameList,
      cookieName,
      cookieParams.hasGoogleAnalyticsTracking,
      env,
    ],
  );

  const rejectCookies = useCallback(() => {
    removeNonRequiredCookies(cookieLists);
    handleClick('acceptRequired');
  }, [cookieLists, handleClick]);

  useListenToApp(rejectCookies, 'tracking:reject');
  // Keep keyboard focus within the banner when it's displayed
  // This ensures users can only interact with banner elements until they make a cookie choice
  useFocusTrap(bannerElement, displayBanner && !displayModal && bannerElement);

  useEffect(() => {
    if (window.HivebriteMobileApp?.v2?.getConfig('trackingDenied')) {
      rejectCookies();
    }

    emitter.on('nativeRejectedUserConsent', rejectCookies);

    return () => {
      emitter.off('nativeRejectedUserConsent', rejectCookies);
    };
  }, [cookieLists, rejectCookies]);

  if (!flagsLoaded || !displayBanner) {
    return null;
  }

  return (
    <>
      {displayBanner && !displayModal && <Overlay />}
      <div
        role="dialog"
        aria-modal="true"
        ref={setBannerRef}
        aria-labelledby="cookie-banner-title"
      >
        <Banner
          bg={frameTheme.backgroundColor}
          data-testid="banner_v2_container"
          role="document"
          tabIndex={0}
        >
          <ThemeProvider theme={env} fontBrightness={frameTheme.fontBrightness}>
            <Box
              width="100%"
              minheight="100%"
              justifyContent={{
                _: 'flex-start',
                m: 'center',
                l: 'center',
                xl: 'center',
              }}
              alignItems="center"
            >
              <Box
                flexDirection="column"
                width={{ _: '85%', m: '85%', l: '85%', xl: '75%' }}
                mt={{ _: 6, m: 0, l: 0, xl: 0 }}
                ml={{ _: 3, m: 0, l: 0, xl: 0 }}
              >
                <Box
                  id="cookie-banner-title"
                  font="h3-semibold"
                  pb={2}
                  mt={{ _: 0, m: 6, l: 0, xl: 0 }}
                >
                  {__('Our website uses cookies')}
                </Box>
                <Box flexDirection="column">
                  {cookieConfig.cookie_agreement_text || null}
                  <Box flexDirection="row" mb={{ _: 0, m: 6, l: 0, xl: 0 }}>
                    {cookieConfig.cookie_agreement_link &&
                      cookieConfig.cookie_agreement_learn_more && (
                        <DefaultLink
                          href={cookieConfig.cookie_agreement_link}
                          target="_blank"
                          alignSelf="flex-start"
                        >
                          {cookieConfig.cookie_agreement_learn_more}
                        </DefaultLink>
                      )}
                    {cookieConfig.cookie_agreement_link_2 &&
                      cookieConfig.cookie_agreement_learn_more_2 && (
                        <DefaultLink
                          href={cookieConfig.cookie_agreement_link_2}
                          target="_blank"
                          ml={2}
                          alignSelf="flex-start"
                        >
                          {cookieConfig.cookie_agreement_learn_more_2}
                        </DefaultLink>
                      )}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box
              width="100%"
              minHeight="100%"
              justifyContent="center"
              alignItems="center"
            >
              <WrapperBtns>
                <Box justifyContent="center" alignItems="center">
                  <Button
                    aria-label="Manage cookie preferences"
                    onClick={() => setDisplayModal(true)}
                    mt={{ _: 3, m: 3, l: 1, xl: 1 }}
                    ml={3}
                  >
                    {__('Manage preferences')}
                  </Button>
                </Box>
                <Box
                  flexDirection={{ _: 'column', m: 'row', l: 'row', xl: 'row' }}
                  justifyContent="flex-end"
                  alignItems={{
                    _: 'flex-start',
                    m: 'center',
                    l: 'center',
                    xl: 'center',
                  }}
                >
                  <Button
                    onClick={() => handleClick('acceptRequired')}
                    ml={3}
                    mt={1}
                  >
                    {__('Accept required')}
                  </Button>
                  <Button
                    onClick={() => handleClick('acceptAll')}
                    ml={3}
                    mt={{ _: 3, m: 1, l: 1, xl: 1 }}
                  >
                    {__('Accept all')}
                  </Button>
                </Box>
              </WrapperBtns>
              <Box
                position="absolute"
                top={{ _: 3, m: 4, l: 5, xl: 4 }}
                right={{ _: 4, m: 8 }}
              >
                <ButtonClose onClick={() => handleClick('acceptRequired')} />
              </Box>
            </Box>
            {displayModal && (
              <CustomiseSettingsModal
                validateSelection={handleClick}
                setDisplayModal={setDisplayModal}
                cookieList={cookieLists.cookieList}
              />
            )}
          </ThemeProvider>
        </Banner>
      </div>
    </>
  );
};

export default CookieBanner;

const Banner = styled(Box).attrs(() => ({
  width: '100%',
  minHeight: { _: 250, m: 150, l: 150, xl: 150 },
  flexDirection: { _: 'column', m: 'row', l: 'row', xl: 'row' },
  justifyContent: 'space-around',
  position: 'fixed',
  bottom: 0,
  left: 0,
  right: 0,
}))`
  z-index: ${({ theme }) => theme.zIndices.modal};
  ${primaryShadow}
`;

const Overlay = styled(Box)`
  background-color: ${({ theme }) => theme.colors.fog};
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: ${({ theme }) => theme.zIndices.modal - 1};
`;

const WrapperBtns = styled(Box).attrs(() => ({
  flexDirection: {
    _: 'column-reverse',
    m: 'column-reverse',
    l: 'row',
    xl: 'row',
  },
  width: { _: '100%', m: '75%', l: '75%', xl: '75%' },
  alignItems: { _: 'flex-start', m: 'flex-end' },
  justifyContent: { _: 'unset', l: 'flex-end' },
  flexWrap: { _: 'wrap', m: 'wrap', l: 'nowrap', xl: 'nowrap' },
  mt: { _: 6, m: 0, l: 5, xl: 5 },
  ml: { _: 0, m: 0, l: 65, xl: 0 },
  mr: { _: 0, m: 86, l: 0, xl: 90 },
  mb: { _: 8, m: 0, l: 0, xl: 0 },
}))``;
